/**
 * PostCard Dialogs — Edit, Delete, Pin dialogs extracted from PostCard
 */
import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { Pin, Loader2 } from 'lucide-react';

export const EditPostDialog = ({ open, onOpenChange, editForm, setEditForm, onSave, saving }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent onClick={(e) => e.stopPropagation()}>
      <DialogHeader>
        <DialogTitle>Edit Post</DialogTitle>
      </DialogHeader>
      <div className="space-y-4 py-4">
        <Input
          placeholder="Title"
          value={editForm.title}
          onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
        />
        <Textarea
          placeholder="Content"
          value={editForm.content}
          onChange={(e) => setEditForm({ ...editForm, content: e.target.value })}
          rows={4}
        />
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
        <Button onClick={onSave} disabled={saving}>
          {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
          Save Changes
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const DeletePostDialog = ({ open, onOpenChange, onDelete, deleting }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent onClick={(e) => e.stopPropagation()}>
      <DialogHeader>
        <DialogTitle>Delete Post</DialogTitle>
        <DialogDescription>
          Are you sure you want to delete this post? This action cannot be undone.
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)} disabled={deleting}>Cancel</Button>
        <Button variant="destructive" onClick={onDelete} disabled={deleting}>
          {deleting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
          Delete
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const PinPostDialog = ({ open, onOpenChange, pinDuration, setPinDuration, onPin, pinning }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent onClick={(e) => e.stopPropagation()} className="max-w-sm">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Pin className="w-5 h-5" /> Pin Post
        </DialogTitle>
        <DialogDescription>
          Choose how long to pin this post to the top of the feed. Max 3 pinned posts allowed.
        </DialogDescription>
      </DialogHeader>
      <div className="py-4 space-y-2">
        {[
          { value: '24h', label: '24 Hours', desc: 'For time-sensitive announcements' },
          { value: '7d', label: '7 Days', desc: 'Default - good for featured content' },
          { value: '30d', label: '30 Days', desc: 'For ongoing campaigns' },
          { value: 'indefinite', label: 'Indefinite', desc: 'Until manually unpinned' },
        ].map((option) => (
          <label
            key={option.value}
            className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
              pinDuration === option.value ? 'border-primary bg-primary/5' : 'border-muted hover:bg-muted/50'
            }`}
          >
            <input
              type="radio"
              name="pinDuration"
              value={option.value}
              checked={pinDuration === option.value}
              onChange={(e) => setPinDuration(e.target.value)}
              className="mt-1"
            />
            <div>
              <div className="font-medium">{option.label}</div>
              <div className="text-xs text-muted-foreground">{option.desc}</div>
            </div>
          </label>
        ))}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)} disabled={pinning}>Cancel</Button>
        <Button onClick={() => onPin(pinDuration)} disabled={pinning}>
          {pinning ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Pin className="w-4 h-4 mr-2" />}
          Pin Post
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
